import React, { useRef, useState } from 'react';
import { Maximize2, PlayCircle, X } from 'lucide-react';
import { S3Modal } from '../v3/primitives';
import { VIDEO_GUIDA_TICKET_ID, VIDEO_GUIDA_TICKET_TITLE, videoGuidaEmbedUrl, videoGuidaWatchUrl } from './videoGuida';

/** Riquadro 16:9 con l'iframe del video (stessi attributi nella modale e nella guida). */
const VideoFrame: React.FC<{ src: string }> = ({ src }) => (
  <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
    <iframe
      src={src}
      title={VIDEO_GUIDA_TICKET_TITLE}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, borderRadius: 10 }}
    />
  </div>
);

/**
 * Modale leggera col video (coda ticket del rappresentante). Usa `S3Modal`,
 * quindi Esc, trappola del fuoco e veste v3/v4 arrivano dal primitivo.
 */
export const VideoGuidaModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const frameBox = useRef<HTMLDivElement>(null);
  // Schermo intero sul contenitore del video; senza Fullscreen API (es. iOS
  // Safari sui div) si apre il video su YouTube in una nuova scheda.
  const goFullscreen = () => {
    const el = frameBox.current as (HTMLDivElement & { webkitRequestFullscreen?: () => void }) | null;
    if (el?.requestFullscreen) {
      el.requestFullscreen().catch(() => { window.open(videoGuidaWatchUrl(), '_blank', 'noopener,noreferrer'); });
    } else if (el?.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else {
      window.open(videoGuidaWatchUrl(), '_blank', 'noopener,noreferrer');
    }
  };
  return (
    <S3Modal open={open} onClose={onClose} labelledBy="s3-video-title">
      <div className="s3-mod">
        <div className="s3-mod__head">
          <h3 className="s3-h3" id="s3-video-title">{VIDEO_GUIDA_TICKET_TITLE}</h3>
          <button type="button" className="s3-pill s3-pill--sm" onClick={goFullscreen} title="Schermo intero">
            <Maximize2 size={14} aria-hidden="true" /> Schermo intero
          </button>
          <button type="button" className="s3-pill s3-pill--sm s3-pill--icon" onClick={onClose} aria-label="Chiudi">
            <X size={14} />
          </button>
        </div>
        <div className="s3-mod__body">
          <div ref={frameBox} className="s3-video-box" style={{ background: '#000', borderRadius: 10 }}>
            <VideoFrame src={videoGuidaEmbedUrl(VIDEO_GUIDA_TICKET_ID)} />
          </div>
        </div>
      </div>
    </S3Modal>
  );
};

/**
 * Card in cima alla guida: copertina con «Riproduci», l'iframe si monta solo al
 * clic (nessuna richiesta a YouTube prima).
 */
export const VideoGuidaCard: React.FC = () => {
  const [playing, setPlaying] = useState(false);
  return (
    <section
      aria-labelledby="video-guida-titolo"
      className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5 mb-4"
    >
      <h2 id="video-guida-titolo" className="text-base font-semibold text-gray-900 dark:text-white">
        Guarda il video guida (11 minuti)
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        Il Centro Ticket spiegato passo per passo, prima di leggere i capitoli della guida.
      </p>
      <div className="mt-3">
        {playing ? (
          <VideoFrame src={videoGuidaEmbedUrl(VIDEO_GUIDA_TICKET_ID, true)} />
        ) : (
          <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
            <button
              type="button"
              onClick={() => setPlaying(true)}
              className="absolute inset-0 w-full h-full rounded-lg bg-gradient-to-br from-emerald-700 to-emerald-900 dark:from-emerald-800 dark:to-gray-900 text-white flex flex-col items-center justify-center gap-2 hover:from-emerald-600 hover:to-emerald-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
            >
              <PlayCircle className="w-12 h-12" aria-hidden="true" />
              <span className="text-sm font-semibold">Riproduci</span>
              <span className="text-xs text-emerald-100/80">Il video si carica da YouTube solo quando lo avvii</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
